import { Link, useNavigate } from "react-router-dom";
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { useState, useEffect } from "react";

export function useMe() {
  const api = useApi();
  const { isSignedIn, isLoaded } = useUser();
  return useQuery({
    queryKey: ["me", isSignedIn],
    enabled: isLoaded && isSignedIn,
    queryFn: async () => (await api.get("/me")).data,
  });
}

const GoldSignIn = ({ children }) => (
  <SignInButton mode="modal">{children}</SignInButton>
);

export default function Nav() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-wine-dark/95 backdrop-blur-md border-b border-gold/20 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <img src="/vivant-monogram.png" alt="VIVANT" className="h-9 w-9 sm:h-10 sm:w-10 object-contain transition-transform duration-500 group-hover:rotate-[8deg]" />
          <span className="font-display text-xl sm:text-2xl tracking-[0.35em] text-parchment">VIVANT</span>
          <span className="hidden sm:inline text-[0.6rem] uppercase tracking-[0.25em] text-gold self-end mb-1">Paris</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-parchment/90 font-sans">
          <Link to="/#evenings" data-testid="nav-evenings" className="link-underline">The Evenings</Link>
          <Link to="/#manifesto" data-testid="nav-manifesto" className="link-underline">Manifesto</Link>
          <Link to="/host" data-testid="nav-host" className="link-underline">Host with us</Link>
          <SignedIn>
            <Link to="/bookings" data-testid="nav-bookings" className="link-underline">My Evenings</Link>
            <Link to="/host/dashboard" data-testid="nav-host-dashboard" className="link-underline">Host</Link>
            {me?.is_admin && (
              <Link to="/admin" data-testid="nav-admin" className="link-underline text-gold">Admin</Link>
            )}
          </SignedIn>
        </nav>

        <div className="flex items-center gap-4">
          <SignedOut>
            <GoldSignIn>
              <button data-testid="nav-signin-btn" className="btn-gold px-5 py-2 text-sm font-medium tracking-wide">
                Sign in
              </button>
            </GoldSignIn>
          </SignedOut>
          <SignedIn>
            <button
              data-testid="nav-bookings-mobile"
              onClick={() => navigate("/bookings")}
              className="md:hidden text-parchment text-sm link-underline"
            >
              Evenings
            </button>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </div>
      </div>
    </header>
  );
}

export { GoldSignIn };
